import React from 'react';
import { Plus, RotateCw, AlertCircle } from 'lucide-react';
import DeviceCard from './DeviceCard';

/**
 * VineyardTab Component
 * Displays vineyard monitoring devices and environmental data
 * 
 * @param {Object} props Component properties
 * @param {Array} props.devices Array of vineyard device objects
 * @param {Array} props.deviceTypes Array of device type definitions
 * @param {Object} props.environmentalData Vineyard environmental data
 * @param {Function} props.onAddDevice Function to call when adding a new device
 * @param {Function} props.onEditDevice Function to call when editing a device
 * @param {Function} props.onDeleteDevice Function to call when deleting a device
 * @param {Function} props.onToggleDevice Function to call when toggling device state
 * @param {Function} props.onRefresh Function to call when refreshing data
 * @param {Date|null} props.lastRefresh Timestamp of last data refresh
 */
const VineyardTab = ({
  devices,
  deviceTypes,
  environmentalData,
  onAddDevice,
  onEditDevice,
  onDeleteDevice,
  onToggleDevice,
  onRefresh,
  lastRefresh
}) => {
  // Sort devices alphabetically by name
  const sortedDevices = [...devices].sort((a, b) => 
    a.deviceName.localeCompare(b.deviceName)
  );

  // Helper to find device type details
  const getDeviceType = (deviceTypeId) => {
    return deviceTypes.find(type => type.typeID === deviceTypeId);
  };
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5>Vineyard Monitoring</h5>
        <div className="btn-group">
          <button
            className="btn btn-outline-secondary"
            onClick={onRefresh}
            title="Refresh data"
          >
            <RotateCw size={16} className="me-1" />
            Refresh
            {lastRefresh && 
              <small className="ms-1 text-muted">
                ({new Date(lastRefresh).toLocaleTimeString()})
              </small>
            }
          </button>
          <button
            className="btn btn-outline-primary"
            onClick={onAddDevice}
          >
            <Plus size={16} className="me-1" />
            Add Vineyard Device
          </button>
        </div>
      </div>

      {/* Environmental Data Summary Cards */}
      <div className="row mb-4">
        <div className="col-md-4">
          <div className="card h-100">
            <div className="card-header">
              <h6 className="card-title mb-0">Rainfall (Last 30 Days)</h6>
            </div>
            <div className="card-body">
              {environmentalData.rainfall.length > 0 ? (
                <div>
                  <div className="d-flex justify-content-between mb-2">
                    <div>
                      <span className="h4">{environmentalData.rainfall[environmentalData.rainfall.length - 1].value}</span> inches
                    </div>
                    <div className="text-muted">
                      Last 7 days: 
                      {environmentalData.rainfall.slice(-7).reduce((sum, day) => sum + day.value, 0).toFixed(1)} inches
                    </div>
                  </div>
                  <div className="progress mb-2" style={{height: '8px'}}>
                    <div 
                      className="progress-bar bg-info" 
                      style={{ 
                        width: `${Math.min(100, (environmentalData.rainfall.slice(-7).reduce((sum, day) => sum + day.value, 0) / 14) * 100)}%` 
                      }}
                    ></div>
                  </div>
                  <small className="text-muted">
                    Monthly Total: {environmentalData.rainfall.reduce((sum, day) => sum + day.value, 0).toFixed(1)} inches
                  </small>
                </div>
              ) : (
                <div className="alert alert-info mb-0">No rainfall data available</div>
              )}
            </div>
          </div>
        </div>
        
        <div className="col-md-4">
          <div className="card h-100">
            <div className="card-header">
              <h6 className="card-title mb-0">Soil Moisture</h6>
            </div>
            <div className="card-body">
              {environmentalData.soilMoisture.length > 0 ? (
                <div>
                  <div className="d-flex justify-content-between mb-2">
                    <div>
                      <span className="h4">{environmentalData.soilMoisture[environmentalData.soilMoisture.length - 1].value}%</span>
                    </div>
                    <div className="text-muted">
                      7-day avg: 
                      {(environmentalData.soilMoisture.slice(-7).reduce((sum, day) => sum + day.value, 0) / 7).toFixed(1)}%
                    </div>
                  </div>
                  <div className="progress mb-2" style={{height: '8px'}}>
                    <div 
                      className={`progress-bar ${
                        environmentalData.soilMoisture[environmentalData.soilMoisture.length - 1].value < 60 ? 'bg-danger' :
                        environmentalData.soilMoisture[environmentalData.soilMoisture.length - 1].value < 75 ? 'bg-warning' : 'bg-success'
                      }`}
                      style={{ 
                        width: `${Math.min(100, environmentalData.soilMoisture[environmentalData.soilMoisture.length - 1].value)}%` 
                      }}
                    ></div>
                  </div>
                  <small className="text-muted">
                    Optimal range: 65% - 85%
                  </small>
                </div>
              ) : (
                <div className="alert alert-info mb-0">No soil moisture data available</div>
              )}
            </div>
          </div>
        </div>
        
        <div className="col-md-4">
          <div className="card h-100">
            <div className="card-header">
              <h6 className="card-title mb-0">Temperature (Last 30 Days)</h6>
            </div>
            <div className="card-body">
              {environmentalData.temperature.length > 0 ? (
                <div>
                  <div className="d-flex justify-content-between mb-2">
                    <div>
                      <span className="h4">{environmentalData.temperature[environmentalData.temperature.length - 1].avg}°F</span>
                    </div>
                    <div>
                      <small className="text-danger">High: {environmentalData.temperature[environmentalData.temperature.length - 1].max}°F</small> / 
                      <small className="text-primary"> Low: {environmentalData.temperature[environmentalData.temperature.length - 1].min}°F</small>
                    </div>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <span className="text-primary small me-2">
                      {Math.min(...environmentalData.temperature.map(d => d.min))}°F
                    </span>
                    <div className="flex-grow-1">
                      <div className="progress" style={{height: '8px'}}>
                        <div 
                          className="progress-bar bg-primary" 
                          style={{ 
                            width: '100%',
                            opacity: 0.3
                          }}
                        ></div>
                      </div>
                    </div>
                    <span className="text-danger small ms-2">
                      {Math.max(...environmentalData.temperature.map(d => d.max))}°F
                    </span>
                  </div>
                  <small className="text-muted">
                    30-day avg: {(
                      environmentalData.temperature.reduce((sum, day) => sum + day.avg, 0) / 
                      environmentalData.temperature.length
                    ).toFixed(1)}°F
                  </small>
                </div>
              ) : (
                <div className="alert alert-info mb-0">No temperature data available</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Vineyard Devices */}
      <h6 className="mb-3">Vineyard Monitoring Devices</h6>
      <div className="row">
        {sortedDevices.length === 0 ? (
          <div className="col-12">
            <div className="alert alert-info">
              <AlertCircle size={18} className="me-2" />
              No vineyard devices configured yet. Click "Add Vineyard Device" to add your first device.
            </div>
          </div>
        ) : (
          sortedDevices.map((device) => (
            <DeviceCard
              key={device.deviceID}
              device={device}
              deviceType={getDeviceType(device.typeID)}
              onEdit={() => onEditDevice(device)}
              onDelete={() => onDeleteDevice(device.deviceID)}
              onToggle={onToggleDevice}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default VineyardTab;